'use client' // Client component

import { CacheProvider } from '@emotion/react'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { usePathname, useServerInsertedHTML } from 'next/navigation'
import { type ReactNode, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { AGGridThemeProvider } from '../components/AGGrid/AGGridThemeProvider'
import Sidebar from '../components/Sidebar'
import UserAccountMenu from '../components/UserAccountMenu'
import createEmotionCache from '../createEmotionCache'
import { isWysehubAdminPath } from '../lib/hostRouting'
import { shouldHideGlobalSidebar } from '../lib/sidebarVisibility'
import ToastStyles from '../styles/ToastStyles'
import theme from '../styles/theme'
import GlobalProvider from './providers'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection time (formerly cacheTime)
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: true, // Refetch when component mounts if data is stale
    },
  },
})

function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname() || ''
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const isAdminRoute = isWysehubAdminPath(pathname)
  const hideSidebar = shouldHideGlobalSidebar(pathname, isDesktop)
  const [isCollapsed, setIsCollapsed] = useState(false)

  if (hideSidebar) {
    return <main>{children}</main>
  }

  return (
    <Box
      // biome-ignore lint/plugin: app shell row hosting the fixed sidebar and content pane
      sx={{ display: 'flex' }}
    >
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <Box
        component="main"
        // biome-ignore lint/plugin: content offset tracks state-driven sidebar width across breakpoints
        sx={{
          flexGrow: 1,
          marginLeft: {
            xs: 0, // No margin for mobile
            sm: 0, // No margin for small tablets
            md: isCollapsed ? '80px' : '250px', // Show margin for large screens
          },
          width: {
            xs: '100%',
            sm: '100%',
            md: `calc(100% - ${isCollapsed ? '80px' : '250px'})`,
          },
          transition: 'margin-left 0.3s ease', // Smooth transition
        }}
      >
        {!isAdminRoute && (
          <Box
            // biome-ignore lint/plugin: account menu sits in a compact top-right header above page content
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              px: 2,
              minHeight: 56,
            }}
          >
            <UserAccountMenu />
          </Box>
        )}
        <Box
          // biome-ignore lint/plugin: AdminLayout already pads its content; other routes need py here
          sx={{ py: isAdminRoute ? 0 : 3 }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Emotion for the App Router, the way MUI's own `AppRouterCacheProvider` does it.
 *
 * Without this, Emotion renders every style it inserts on the server as an inline
 * `<style>` in the body, next to the element that needed it. React skips those while
 * hydrating in most places, but not on the marketing pages' server-rendered tree, where
 * MUI's baseline `<style>` landed where React expected the shell's `<main>` and every
 * marketing page failed to hydrate. In `compat` mode Emotion inserts nothing inline;
 * the names inserted during a server render are collected here and flushed into
 * `<head>` through `useServerInsertedHTML`, so the body holds only what React rendered.
 */
function useEmotionRegistry(nonce: string | undefined) {
  const [registry] = useState(() => {
    const cache = createEmotionCache(nonce)
    cache.compat = true
    const prevInsert = cache.insert
    let inserted: Array<{ name: string; isGlobal: boolean }> = []
    cache.insert = (...args) => {
      const [selector, serialized] = args
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push({ name: serialized.name, isGlobal: !selector })
      }
      return prevInsert(...args)
    }
    const flush = () => {
      const previous = inserted
      inserted = []
      return previous
    }
    return { cache, flush }
  })

  useServerInsertedHTML(() => {
    const inserted = registry.flush()
    if (inserted.length === 0) return null
    const { cache } = registry
    let styles = ''
    let dataEmotionAttribute = cache.key
    const globals: Array<{ name: string; style: string }> = []
    for (const { name, isGlobal } of inserted) {
      const style = cache.inserted[name]
      if (typeof style !== 'string') continue
      if (isGlobal) {
        globals.push({ name, style })
      } else {
        styles += style
        dataEmotionAttribute += ` ${name}`
      }
    }
    return (
      <>
        {globals.map(({ name, style }) => (
          <style
            key={name}
            nonce={nonce}
            data-emotion={`${cache.key}-global ${name}`}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Emotion's own serialised CSS, flushed into the head on the server
            dangerouslySetInnerHTML={{ __html: style }}
          />
        ))}
        {styles ? (
          <style
            nonce={nonce}
            data-emotion={dataEmotionAttribute}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Emotion's own serialised CSS, flushed into the head on the server
            dangerouslySetInnerHTML={{ __html: styles }}
          />
        ) : null}
      </>
    )
  })

  return registry.cache
}

export default function ClientProviders({
  children,
  nonce,
}: {
  children: ReactNode
  nonce?: string
}) {
  const cache = useEmotionRegistry(nonce)

  return (
    <QueryClientProvider client={queryClient}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AGGridThemeProvider>
            <GlobalProvider>
              <AppShell>{children}</AppShell>
            </GlobalProvider>
          </AGGridThemeProvider>
          <ToastStyles />
          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            draggable
          />
          {/*
            To add React Query DevTools for debugging, install the package and uncomment:
            npm install @tanstack/react-query-devtools

            Then add this before the closing ThemeProvider tag:
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
          */}
        </ThemeProvider>
      </CacheProvider>
    </QueryClientProvider>
  )
}
